import React, { useState, useRef, useEffect } from 'react';

const SimpleDateTimePicker = ({ 
  value, 
  onChange, 
  placeholder = "Select time",
  minDate = null,
  className = ""
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const pickerRef = useRef(null);

  useEffect(() => {
    const checkDesktop = () => window.innerWidth >= 768;
    setIsDesktop(checkDesktop());
    
    const handleResize = () => setIsDesktop(checkDesktop());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCalendar]);

  const formatDateTimeLocal = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const pad = (num) => num.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const formatDisplayValue = () => {
    if (!value) return '';
    const date = new Date(value);
    return date.toLocaleString([], { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
  };

  const handleInputClick = () => {
    if (isDesktop) {
      setShowCalendar(true);
      if (value) {
        const date = new Date(value);
        setSelectedDate(date);
        setCurrentMonth(new Date(date.getFullYear(), date.getMonth()));
      }
    }
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    newDate.setHours(selectedDate.getHours(), selectedDate.getMinutes());
    setSelectedDate(newDate);
  };

  const handleTimeChange = (hours, minutes) => {
    const newDate = new Date(selectedDate);
    newDate.setHours(hours, minutes);
    setSelectedDate(newDate);
  };

  const handleConfirm = () => {
    onChange(selectedDate.toISOString());
    setShowCalendar(false);
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateDisabled = (day) => {
    if (!minDate) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date < new Date(minDate);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isSelected = selectedDate.getDate() === day && 
                        selectedDate.getMonth() === currentMonth.getMonth() && 
                        selectedDate.getFullYear() === currentMonth.getFullYear();
      const isDisabled = isDateDisabled(day);
      
      days.push(
        <button
          key={day}
          onClick={() => !isDisabled && handleDateSelect(day)}
          disabled={isDisabled}
          className={`w-8 h-8 text-sm rounded ${
            isSelected 
              ? 'bg-yellow-500 text-white' 
              : isDisabled 
                ? 'text-gray-300 cursor-not-allowed'
                : 'hover:bg-gray-100'
          }`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const renderTimePicker = () => {
    const hours12 = Array.from({ length: 12 }, (_, i) => i + 1); // 1-12
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 5-minute intervals: 0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55
    const periods = ['AM', 'PM'];

    const selectedHour24 = selectedDate.getHours();
    const selectedHour12 = selectedHour24 === 0 ? 12 : selectedHour24 > 12 ? selectedHour24 - 12 : selectedHour24;
    const selectedPeriod = selectedHour24 < 12 ? 'AM' : 'PM';

    const handleHourChange = (hour12) => {
      let hour24 = hour12;
      if (selectedPeriod === 'AM' && hour12 === 12) hour24 = 0;
      else if (selectedPeriod === 'PM' && hour12 !== 12) hour24 = hour12 + 12;
      handleTimeChange(hour24, selectedDate.getMinutes());
    };

    const handlePeriodChange = (period) => {
      let hour24 = selectedHour12;
      if (period === 'AM' && selectedHour12 === 12) hour24 = 0;
      else if (period === 'PM' && selectedHour12 !== 12) hour24 = selectedHour12 + 12;
      else if (period === 'AM' && selectedHour12 !== 12) hour24 = selectedHour12;
      else if (period === 'PM' && selectedHour12 === 12) hour24 = 12;
      handleTimeChange(hour24, selectedDate.getMinutes());
    };

    return (
      <div className="flex gap-3 items-center justify-center mt-4">
        <div className="text-center">
          <label className="block text-xs font-medium mb-2">Hour</label>
          <div className="h-24 overflow-y-auto border rounded w-12">
            {hours12.map(hour => (
              <button
                key={hour}
                onClick={() => handleHourChange(hour)}
                className={`w-full py-1 text-sm ${
                  selectedHour12 === hour 
                    ? 'bg-yellow-500 text-white' 
                    : 'hover:bg-gray-100'
                }`}
              >
                {hour}
              </button>
            ))}
          </div>
        </div>
        <div className="text-center">
          <label className="block text-xs font-medium mb-2">Min</label>
          <div className="h-24 overflow-y-auto border rounded w-12">
            {minutes.map(minute => (
              <button
                key={minute}
                onClick={() => handleTimeChange(selectedDate.getHours(), minute)}
                className={`w-full py-1 text-sm ${
                  selectedDate.getMinutes() === minute 
                    ? 'bg-yellow-500 text-white' 
                    : 'hover:bg-gray-100'
                }`}
              >
                {minute.toString().padStart(2, '0')}
              </button>
            ))}
          </div>
        </div>
        <div className="text-center">
          <label className="block text-xs font-medium mb-2">Period</label>
          <div className="h-24 border rounded w-12 flex flex-col">
            {periods.map(period => (
              <button
                key={period}
                onClick={() => handlePeriodChange(period)}
                className={`flex-1 text-sm ${
                  selectedPeriod === period 
                    ? 'bg-yellow-500 text-white' 
                    : 'hover:bg-gray-100'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Mobile: Use native datetime-local (unchanged - perfect!)
  if (!isDesktop) {
    return (
      <input
        type="datetime-local"
        value={formatDateTimeLocal(value)}
        onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : '')}
        min={minDate ? formatDateTimeLocal(minDate) : undefined}
        placeholder={placeholder}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white text-black ${className}`}
      />
    );
  }

  // Desktop: Custom calendar popup
  return (
    <div className="relative" ref={pickerRef}>
      <input
        type="text"
        value={formatDisplayValue()}
        placeholder={placeholder}
        onClick={handleInputClick}
        readOnly
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white text-black cursor-pointer ${className}`}
      />
      
      {showCalendar && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-80">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-1 hover:bg-gray-100 rounded"
            >
              ◀
            </button>
            <h3 className="font-medium">
              {currentMonth.toLocaleDateString([], { month: 'long', year: 'numeric' })}
            </h3>
            <button 
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-1 hover:bg-gray-100 rounded"
            >
              ▶
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-xs font-medium text-gray-500 text-center p-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {renderCalendar()}
          </div>

          {/* Time Picker */}
          {renderTimePicker()}

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={() => setShowCalendar(false)}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 px-3 py-2 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
            >
              Confirm
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SimpleDateTimePicker;
